import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import {
  MoneyCell,
  SkuCell,
  TransactionLineTable,
  type LineColumn,
} from '../components/TransactionLineTable.tsx';
import { apiFetch } from '../lib/api.ts';
import { dateOnly, money, num, statusClass, statusLabel } from '../lib/format.ts';

interface Line {
  lineNo: number;
  productCode: string | null;
  description: string | null;
  qtyOrdered: number;
  actualReceivedQty: number;
  unitPrice: number;
  subTotal: number;
}

interface Detail {
  id: number;
  poNo: string;
  poDate: string;
  creditorNo: string | null;
  creditorName: string | null;
  currency: string;
  value: number;
  status: string;
  lines: Line[];
}

export function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['purchase-order', id],
    queryFn: () =>
      apiFetch<Detail>(`/api/dashboard/purchase-orders/${encodeURIComponent(id ?? '')}`),
    enabled: !!id,
  });

  const productLines = (data?.lines ?? []).filter((l) => l.productCode);
  const columns: LineColumn<Line>[] = [
    { label: '#', className: 'mono', render: (l) => l.lineNo },
    { label: 'SKU', className: 'mono', render: (l) => <SkuCell code={l.productCode} /> },
    { label: 'Description', render: (l) => l.description || '—' },
    { label: 'Qty ordered', className: 'num', render: (l) => num(l.qtyOrdered) },
    { label: 'Received', className: 'num', render: (l) => num(l.actualReceivedQty) },
    { label: 'Unit price', className: 'num', render: (l) => <MoneyCell amount={l.unitPrice} currency={data?.currency ?? 'MYR'} /> },
    { label: 'Subtotal', className: 'num', render: (l) => <MoneyCell amount={l.subTotal} currency={data?.currency ?? 'MYR'} /> },
  ];

  return (
    <>
      <Link to="/purchase-orders" className="back-link">← All purchase orders</Link>
      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}
      {data && (
        <>
          <header className="main-header">
            <div>
              <h1 className="main-title">PO {data.poNo}</h1>
              <span className="main-subtitle mono">{dateOnly(data.poDate)}</span>
            </div>
            <span className={`badge ${statusClass(data.status)}`}>{statusLabel(data.status)}</span>
          </header>
          <section className="panel">
            <header className="panel-header"><h2 className="panel-title">Header</h2></header>
            <dl className="detail-grid">
              <dt>PO no</dt><dd className="mono">{data.poNo}</dd>
              <dt>Date</dt><dd>{dateOnly(data.poDate)}</dd>
              <dt>Supplier</dt>
              <dd>
                {data.creditorNo ? (
                  <Link to={`/suppliers/${encodeURIComponent(data.creditorNo)}`}>
                    {data.creditorName ?? data.creditorNo}
                  </Link>
                ) : '—'}
              </dd>
              <dt>Value</dt><dd>{money(data.value, data.currency)}</dd>
            </dl>
          </section>
          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Line items</h2>
              <span className="panel-subtitle">{productLines.length} item{productLines.length === 1 ? '' : 's'}</span>
            </header>
            <TransactionLineTable lines={productLines} columns={columns} />
          </section>
        </>
      )}
    </>
  );
}
